import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { FileUpload } from 'components/FileUpload'
import { useTranslation } from 'react-i18next'
import { WorkRatesTable } from './work-rates-table'
import { WeightSliders } from './weight-sliders'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { ILot } from 'types/lots'
import { DeepPartial, useFormContext, Controller } from 'react-hook-form'
import { RequiredStar } from 'components/RequiredStarSpan'
import { TechnicalProposal } from 'pages/tender/technical-proposal'
import { useParams } from 'react-router-dom'
import { useTenderQuery } from 'services/tender'
import { ETenderType } from 'types/tender'
import { Loading } from 'components/Loading'
import { useEffect, useState } from 'react'

type Props = {
  lot: ILot
  isSubmissionTimePassed: boolean
}

export const TenderProposalForm = ({ lot, isSubmissionTimePassed }: Props) => {
  const { t } = useTranslation()
  const { tenderId } = useParams()
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<DeepPartial<ILot>>()
  const [isDisabled, setIsDisabled] = useState(false)

  const { data: tender, isLoading, isError } = useTenderQuery({ id: tenderId ?? '' })

  const validityDurationDays = watch('procurementRequirement.validityDurationDays')

  useEffect(() => {
    setIsDisabled(isSubmissionTimePassed)
  }, [isSubmissionTimePassed])

  useEffect(() => {
    if (validityDurationDays) {
      setValue(
        'procurementRequirement.guaranteeValidityDurationDays',
        Number(validityDurationDays) + 30
      )
    }
  }, [validityDurationDays])

  if (isLoading) {
    return <Loading fullPage={false} />
  }

  return (
    <>
      <CollapsibleSection title={t('tender-proposal')}>
        <Typography variant="body2">{t('tender-proposal-description')}</Typography>

        <WeightSliders isDisabled={isDisabled} />

        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMore color="secondary" />}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: 'white',
              overflow: 'hidden',
            }}
          >
            <Typography sx={{ width: '50%', flexShrink: 0 }}>
              {t('price-proposal')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
              <Controller
                control={control}
                name="procurementRequirement.priceDescription"
                render={({ field }) => (
                  <TextField
                    {...field}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label={
                      <>
                        {t('price-proposal-description')}{' '}
                        <RequiredStar>*</RequiredStar>
                      </>
                    }
                    multiline
                    rows={3}
                    type="text"
                    variant="outlined"
                    disabled={isDisabled}
                  />
                )}
              />
            </FormControl>
            <Paper sx={{ p: 1 }}>
              <Grid container>
                <Grid item xs={6}>
                  <Typography>
                    {t('tender-proposal-expiration-duration')}:
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Box display="flex" alignItems="center">
                    <Typography>{t('tender-proposal-expiration-part1')}</Typography>
                    <Controller
                      control={control}
                      name="procurementRequirement.validityDurationDays"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          type="number"
                          InputProps={{ inputProps: { min: 30 } }}
                          sx={{
                            mx: 1,
                            width: 60,
                            '& input': { p: 0.25, textAlign: 'center' },
                          }}
                          disabled={isDisabled}
                        />
                      )}
                    />
                    <Typography>{t('tender-proposal-expiration-part2')}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            {tender?.method !== ETenderType.PRICE_INQUIRY && (
              <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                <Controller
                  control={control}
                  name="procurementRequirement.guaranteeAmount"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('tender-proposal-warranty-amount')}
                      type="number"
                      variant="outlined"
                      disabled={isDisabled}
                    />
                  )}
                />
              </FormControl>
            )}

            {tender?.method !== ETenderType.PRICE_INQUIRY && (
              <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                <Controller
                  control={control}
                  name="procurementRequirement.guaranteeValidityDurationDays"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      InputProps={{ inputProps: { min: 30 } }}
                      label={t('tender-proposal-validity-duration')}
                      type="number"
                      variant="outlined"
                      disabled
                    />
                  )}
                />
              </FormControl>
            )}

            <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
              <Controller
                control={control}
                name="procurementRequirement.executionGuarantee"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('warranty-of-procurement-contract-action')}
                    multiline
                    minRows={3}
                    variant="outlined"
                    disabled={isDisabled}
                  />
                )}
              />
            </FormControl>
            <WorkRatesTable
              procurementRequirementId={lot.procurementRequirement.id}
              isDisabled={isDisabled}
            />

            <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
              <Controller
                control={control}
                name="procurementRequirement.extraInfo"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('extra-info-short')}
                    multiline
                    minRows={4}
                    variant="outlined"
                    disabled={isDisabled}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
              <Controller
                control={control}
                name="procurementRequirement.extraFileId"
                render={({ field }) => (
                  <FileUpload
                    text={t('extra-document')}
                    handleFile={(file) => {
                      setValue('procurementRequirement.extraFileId', file.id)
                    }}
                    onDelete={() =>
                      setValue('procurementRequirement.extraFileId', null)
                    }
                    fileId={field.value ?? undefined}
                    isDisabled={isDisabled}
                  />
                )}
              />
            </FormControl>
          </AccordionDetails>
        </Accordion>

        <TechnicalProposal isDisabled={isDisabled} />
      </CollapsibleSection>
    </>
  )
}
